/**
 * The query parameters a reply was actually built from.
 *
 * The agent appends these as a trailing JSON block so the host can reconcile
 * its own controls. For a reader they are provenance rather than content, so
 * they start collapsed: worth being able to check, not worth reading.
 *
 * Recognised by shape rather than by position. A `code` renderer is handed one
 * fence at a time and cannot know which is last, and treating every JSON block
 * as applied parameters would swallow ones that are part of the answer.
 */

import React, { useState } from "react";
import PropTypes from "prop-types";
import { FiChevronDown, FiFilter } from "react-icons/fi";

const KEYS = [
  "brands",
  "brand_id",
  "markets",
  "market_id",
  "module",
  "period",
  "segments",
  "filter_codes",
];

/** Whether a parsed JSON block looks like applied parameters. */
export const isAppliedFilters = (data) =>
  Boolean(data) &&
  typeof data === "object" &&
  !Array.isArray(data) &&
  KEYS.some((key) => key in data);

const one = (value) =>
  value && typeof value === "object"
    ? // A filter selection: its code, and the values chosen within it.
      [value.code, (value.options ?? []).join(", ")].filter(Boolean).join(" · ")
    : String(value);

const list = (value) =>
  Array.isArray(value) ? value.map(one).join(", ") : one(value);

/**
 * Folds a list of retrievals into the one set of rows this chip shows.
 *
 * The contract sends an array; an older block sends a bare object. Both end up
 * here, and a reply built from several queries is summarised as everything it
 * touched — the panel is where the per-query split belongs, not a chip.
 */
const merge = (data) => {
  const runs = Array.isArray(data) ? data : [data];
  const out = {};
  for (const run of runs) {
    if (!run || typeof run !== "object") continue;
    for (const [key, value] of Object.entries(run)) {
      if (value === null || value === undefined || value === "") continue;
      if (Array.isArray(value)) {
        // Unique across queries: the same brand asked for twice is one brand.
        const seen = new Set((out[key] ?? []).map((v) => JSON.stringify(v)));
        out[key] = [
          ...(out[key] ?? []),
          ...value.filter((v) => !seen.has(JSON.stringify(v))),
        ];
      } else if (!(key in out)) {
        out[key] = value;
      }
    }
  }
  return out;
};

/** A period reads as its dates when it has them, otherwise as its name. */
const describePeriod = (period) => {
  if (!period || typeof period !== "object") return String(period ?? "");
  const start = period.start ?? period.start_date;
  const end = period.end ?? period.end_date;
  if (start && end) return `${start} to ${end}`;
  return period.preset ?? period.type ?? "";
};

const LABELS = {
  modules: "Modules",
  module: "Module",
  screen: "Screen",
  brands: "Brands",
  brand_id: "Brands",
  competitors: "Compared against",
  markets: "Markets",
  market_id: "Markets",
  segments: "Segments",
  filters: "Filters",
  filter_codes: "Filters",
  audience_base: "Base",
  stat_test: "Stat test",
  analysis_mode: "Mode",
  interval: "Interval",
  time_period: "Period",
  period: "Period",
  comparison_period: "Against",
};

/** Keys whose value is a period rather than a list. */
const PERIOD_KEYS = new Set(["time_period", "period", "comparison_period"]);

const AppliedFilters = ({ data }) => {
  const [open, setOpen] = useState(false);

  const rows = Object.entries(merge(data))
    .filter(([, value]) => value !== null && value !== undefined && value !== "")
    .map(([key, value]) => [
      LABELS[key] ?? key,
      PERIOD_KEYS.has(key) ? describePeriod(value) : list(value),
    ])
    .filter(([, value]) => value !== "");

  if (!rows.length) return null;

  return (
    <div className="lou-applied">
      <button
        type="button"
        className="lou-applied__toggle"
        onClick={() => setOpen((wasOpen) => !wasOpen)}
        aria-expanded={open}
      >
        <FiFilter size={12} aria-hidden="true" />
        <span>Applied filters</span>
        <FiChevronDown
          size={13}
          aria-hidden="true"
          className={`lou-applied__chevron ${open ? "is-open" : ""}`}
        />
      </button>

      {open ? (
        <dl className="lou-applied__list">
          {rows.map(([label, value]) => (
            <div className="lou-applied__row" key={label}>
              <dt className="lou-applied__key">{label}</dt>
              <dd className="lou-applied__value">{value}</dd>
            </div>
          ))}
        </dl>
      ) : null}
    </div>
  );
};

AppliedFilters.propTypes = {
  data: PropTypes.oneOfType([PropTypes.object, PropTypes.array]).isRequired,
};

export default AppliedFilters;
